import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import React, { useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import PosCartContainer from '../../features/order-review/PosCartContainer';
import { selectCheckEventItems } from '../../selectors/order';
import ConsoleCard from './ConsoleCard';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  // Scrolls the POS table internally so the scrollbar stays inside the card.
  // Shared 8px left/right inset so the table lines up with the card header icon.
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingLeft: HITL3_PADDING,
    paddingRight: HITL3_PADDING,
  },
}));

// Mirrors RightBar's conversion for the POS cart panel.
const convertCheckEventToOrderItems = (checkEventItems: any[]) => {
  const convertOptions = (options: any[]): any[] =>
    options?.map((option: any) => ({
      name: option.name,
      pos_id: option.pos_id?.toString() || option.id?.toString() || '',
      quantity: option.quantity || 1,
      price: option.price || '0.00',
      options: convertOptions(option.options || []),
    })) || [];

  return checkEventItems.map((item) => ({
    name: item.name,
    pos_id: item.pos_id?.toString() || item.id?.toString() || '',
    quantity: item.quantity || 1,
    price: item.price || '0.00',
    options: convertOptions(item.options || []),
  }));
};

interface PosCartCardProps {
  // Controlled collapse so the parent drives the resizable panel; falls back to
  // internal state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * POS CART card (right pane, bottom). The POS-side view of the order
 * (PosCartContainer's Items / Pos Id / Price table) under a "POS Cart" heading.
 * The old blue dot + inline title are dropped — the ConsoleCard header (receipt
 * icon + label + chevron) owns them now (see PosCartContainer's hitl_3_0 branch).
 * Rendered only when the pos_cart flag is on (RightSidebar decides).
 */
const PosCartCard = ({
  open: openProp,
  onOpenChange,
}: PosCartCardProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const checkEventItems = useAppSelector(selectCheckEventItems);
  const orderItems = React.useMemo(
    () => convertCheckEventToOrderItems(checkEventItems),
    [checkEventItems]
  );

  return (
    <ConsoleCard
      icon={<ReceiptLongOutlinedIcon />}
      title="POS Cart"
      open={open}
      onToggle={toggleOpen}
      testId="pos-cart-card"
      // Keep the POS table mounted (hidden) so expanding it is instant.
      keepBodyMounted
    >
      <div className={classes.body}>
        <PosCartContainer items={orderItems} />
      </div>
    </ConsoleCard>
  );
};

export default PosCartCard;
